import { DateTime } from 'luxon';
import { AcceptableInvitationResponseDto, CreateRecoveryIdentityDto, UserPasswordChangeClientFinalDto, UserPasswordChangeServerFirstDto, UserResponseDto, } from '@lucidlink/dto/web-service-api';
import { base64ToArray, decryptWithAes } from '@lucidlink/ts-crypto/crypto';
import { parserFromDto } from '#fetcher/parse-utils';
import { AccountCrypto, deriveUserIdentity, prepareClientFinal, prepareClientFirst, } from '#sdk/common';
import { assertIsUser, } from '#session/session-context-data';
import { remapUserDto, } from '#types/sdk';
import { BACKUP_CODE_GENERATED } from '#types/sdk/telemetry/event-names.ts';
import { prepareAcceptIndividualInvitationDto, prepareAcceptOrganizationInvitationDto, } from './invitations/prepare-accept-invitation-dto';
export { prepareAcceptOrganizationInvitationDto as prepareAcceptInvitationDto } from './invitations/prepare-accept-invitation-dto';
export const createCurrentUserManagement = ({ getAuthenticatedFetcher, getAccountDetails, getSessionManager, getTelemetryClient, }) => {
    return new CurrentUserManagementImpl(getAuthenticatedFetcher, getAccountDetails, getSessionManager, getTelemetryClient);
};
class CurrentUserManagementImpl {
    getGlobalFetcher;
    getAccountDetails;
    getSessionManager;
    getTelemetryClient;
    mfaFetcher;
    constructor(getGlobalFetcher, getAccountDetails, getSessionManager, getTelemetryClient) {
        this.getGlobalFetcher = getGlobalFetcher;
        this.getAccountDetails = getAccountDetails;
        this.getSessionManager = getSessionManager;
        this.getTelemetryClient = getTelemetryClient;
        this.mfaFetcher = this.getGlobalFetcher().createSubFetcher({
            path: ['users', 'me', 'mfa'],
        });
    }
    async getProfile() {
        const result = await this.getGlobalFetcher().get(['users', 'me'], {
            parser: parserFromDto(UserResponseDto),
        });
        return remapUserDto(result);
    }
    dismissPendingInvitation() {
        return this.getGlobalFetcher().delete([
            'users',
            'me',
            'pending-invitation',
        ]);
    }
    declineInvitation(workspaceId) {
        return this.getGlobalFetcher().delete([
            'workspaces',
            workspaceId,
            'members',
            'pending',
            'me',
        ]);
    }
    async getInvitation(invitationId) {
        return await this.getGlobalFetcher().get(['invitations', invitationId], {
            parser: parserFromDto(AcceptableInvitationResponseDto),
        });
    }
    async changePasswordAndLogout(params) {
        const accountDetails = await this.getAccountDetails();
        assertIsUser(accountDetails);
        const clientFirstMessage = prepareClientFirst(accountDetails.email);
        const serverFirst = await this.getGlobalFetcher().post(['users', accountDetails.userId, 'password-change/first'], {
            body: clientFirstMessage,
            parser: parserFromDto(UserPasswordChangeServerFirstDto),
        });
        const scramClientFinalMessagePromise = prepareClientFinal(clientFirstMessage, serverFirst, params.oldPassword);
        const userIdentityPromise = deriveUserIdentity(params.newPassword, accountDetails);
        await this.getGlobalFetcher().post(['users', accountDetails.userId, 'password-change/final'], {
            body: UserPasswordChangeClientFinalDto.create({
                ...(await scramClientFinalMessagePromise),
                ...(await userIdentityPromise),
            }),
        });
        await this.getSessionManager().logout();
    }
    async acceptInvitation(invitationData) {
        const acceptableInvitationData = await this.getInvitation(invitationData.id);
        const accountDetails = await this.getAccountDetails();
        assertIsUser(accountDetails);
        const accountCrypto = new AccountCrypto(accountDetails);
        const userKey = await accountCrypto.getAccountKey();
        const linkSecret = await getLinkSecret(invitationData, userKey);
        if (acceptableInvitationData.member) {
            await this.acceptIndividualInvitation({
                userKey,
                linkSecret,
                acceptableInvitationData,
                member: acceptableInvitationData.member,
                email: accountDetails.email,
            });
        }
        else {
            await this.acceptOrganizationInvitation({
                userKey,
                linkSecret,
                acceptableInvitationData,
            });
        }
    }
    async acceptIndividualInvitation(params) {
        const { userKey, linkSecret, acceptableInvitationData, member, email } = params;
        const acceptInvitation = await prepareAcceptIndividualInvitationDto({
            userKey,
            linkSecret,
            acceptableInvitationData,
            member,
        });
        await this.getGlobalFetcher().patch([
            'workspaces',
            acceptableInvitationData.workspace.id,
            'invitations',
            'individual',
            encodeURIComponent(email),
            'members',
        ], {
            body: acceptInvitation,
        });
    }
    async acceptOrganizationInvitation(params) {
        const { userKey, linkSecret, acceptableInvitationData } = params;
        const acceptInvitation = await prepareAcceptOrganizationInvitationDto({
            userKey,
            acceptableInvitationData,
            linkSecret,
        });
        await this.getGlobalFetcher().post([
            'workspaces',
            acceptableInvitationData.workspace.id,
            'invitations',
            'organization',
            'members',
        ], {
            body: acceptInvitation,
        });
    }
    async setBackupCode(backupCode) {
        const accountDetails = await this.getAccountDetails();
        assertIsUser(accountDetails);
        const userIdentity = await deriveUserIdentity(backupCode, accountDetails);
        await this.getGlobalFetcher().post(['users', accountDetails.userId, 'recovery'], { body: CreateRecoveryIdentityDto.create(userIdentity) });
        this.logUserEvent({
            name: BACKUP_CODE_GENERATED,
            data: { clientEventTime: DateTime.local().toISO() },
        });
    }
    async deleteBackupCode() {
        const accountDetails = await this.getAccountDetails();
        assertIsUser(accountDetails);
        await this.getGlobalFetcher().delete([
            'users',
            accountDetails.userId,
            'recovery',
        ]);
    }
    logUserEvent(event) {
        this.getTelemetryClient().sendEvent(event);
    }
    async createTotp(params) {
        await this.mfaFetcher.post(['totp'], {
            body: params,
        });
    }
    async deleteTotp(token) {
        await this.mfaFetcher.delete(['totp'], {
            queryParams: {
                token,
            },
        });
    }
}
const getLinkSecret = async (invitationData, userKey) => {
    if (!invitationData.isEncrypted) {
        return base64ToArray(invitationData.linkSecret);
    }
    const linkSecretArray = await decryptWithAes(invitationData.linkSecret, userKey);
    return linkSecretArray.buffer;
};
export * from './invitations/prepare-accept-invitation-dto';
export * from './invitations/prepare-accept-sso-invitation-dto';
